// Shared BSV transaction status timing and state logic

export const BSV_TX_STATES = {
    PENDING: 'pending',
    AWAITING_RESUBMISSION: 'awaiting-resubmission',
    AWAITING_CONFIRMATION: 'awaiting-confirmation',
    CONFIRMED: 'confirmed',
    FAILED: 'failed', // Failed and won't retry (exhausted retries or cancelled)
}

const PENDING_STATUSES = new Set(['started', 'broadcasting', 'broadcasted', 'pending', 'finished'])

const FINALIZED_SUCCESS_STATUSES = new Set(['confirmed', 'completed'])
const FINALIZED_FAILURE_STATUSES = new Set(['failed', 'cancelled'])

const computePendingState = createdAt => {
    if (!createdAt) {
        return BSV_TX_STATES.PENDING
    }

    const timeSinceCreation = (Date.now() - new Date(createdAt).getTime()) / 1000

    if (timeSinceCreation > 40) {
        return BSV_TX_STATES.AWAITING_RESUBMISSION
    }

    return BSV_TX_STATES.PENDING
}

/**
 * Calculate BSV transaction state based on timing and status
 * @param {Object} bsvTx - The BSV transaction object
 * @param {string} txId - The transaction ID
 * @returns {string} One of BSV_TX_STATES
 */
export const getBsvTransactionState = (bsvTx, txId) => {
    const effectiveTxId = txId || bsvTx?.tx_id || null

    if (!bsvTx) {
        return effectiveTxId
            ? BSV_TX_STATES.AWAITING_CONFIRMATION
            : BSV_TX_STATES.AWAITING_RESUBMISSION
    }

    const status = bsvTx.status

    if (FINALIZED_SUCCESS_STATUSES.has(status)) {
        return BSV_TX_STATES.CONFIRMED
    }

    if (FINALIZED_FAILURE_STATUSES.has(status)) {
        // Check if this is a permanent failure (exhausted retries or cancelled)
        if (status === 'cancelled') {
            return BSV_TX_STATES.FAILED
        }
        if (status === 'failed' && bsvTx.retries >= bsvTx.max_retries) {
            return BSV_TX_STATES.FAILED
        }
        // Still have retries left - will be resubmitted
        return BSV_TX_STATES.AWAITING_RESUBMISSION
    }

    if (PENDING_STATUSES.has(status)) {
        if ((status === 'broadcasted' || status === 'finished') && effectiveTxId) {
            return BSV_TX_STATES.AWAITING_CONFIRMATION
        }
        if (effectiveTxId && status !== 'started') {
            return BSV_TX_STATES.AWAITING_CONFIRMATION
        }
        return computePendingState(bsvTx.created_at)
    }

    if (!effectiveTxId) {
        return BSV_TX_STATES.AWAITING_RESUBMISSION
    }

    return BSV_TX_STATES.CONFIRMED
}

/**
 * Get tooltip text for BSV transaction state
 * @param {Object} bsvTx - The BSV transaction object
 * @param {string} txId - The transaction ID
 * @param {string} state - The transaction state from getBsvTransactionState
 * @returns {string} Tooltip text
 */
export const getBsvTransactionTooltip = (bsvTx, txId, state) => {
    switch (state) {
        case BSV_TX_STATES.PENDING:
            return 'Transaction is being processed'
        case BSV_TX_STATES.AWAITING_RESUBMISSION:
            const retryCount = bsvTx?.retries || 0
            const maxRetries = bsvTx?.max_retries || 5
            const retryInfo = retryCount > 0 ? ` (retry ${retryCount} of ${maxRetries})` : ''
            return `Treechat is preparing to post this onchain${retryInfo}`
        case BSV_TX_STATES.AWAITING_CONFIRMATION:
            return `Txid: ${bsvTx?.tx_id || txId || 'No Transaction ID'}`
        case BSV_TX_STATES.FAILED:
            if (bsvTx?.status === 'cancelled') {
                return 'Transaction cancelled'
            }
            const failedRetries = bsvTx?.retries || 0
            const failedMaxRetries = bsvTx?.max_retries || 5
            return `Transaction failed after ${failedRetries} of ${failedMaxRetries} retries`
        case BSV_TX_STATES.CONFIRMED:
        default:
            const effectiveTxId = bsvTx?.tx_id || txId
            return `Txid: ${effectiveTxId || 'No Transaction ID'}`
    }
}
